/**
 * AGRUPAR Y REESCALAR A OTRA ZONA.
 *
 * Dos operaciones distintas que comparten el mismo problema: mover un conjunto
 * de capas sin que se descoloquen entre ellas.
 *
 * **Agrupar** no crea un tipo de capa nuevo. Se apoya en `name`, que ya existe
 * en el esquema desde la version 2: las capas de un grupo comparten una
 * etiqueta, y el editor las selecciona juntas. Un `GroupLayer` de verdad
 * obligaria a subir el esquema, a escribir un migrador y a que el compositor de
 * produccion aprendiera a recorrer un arbol. Con esto, para el taller siguen
 * siendo las mismas capas planas de siempre.
 *
 * **Reescalar a otra zona** es lo que hace posible «lo mismo pero en sudadera»,
 * que es una peticion constante y hoy obliga a rehacer el diseno entero.
 *
 * Aritmetica pura en milimetros, sin Fabric ni DOM.
 */
import { layerBBoxMm, type Layer } from '@besbello/design-core';

/** Prefijo de la etiqueta de grupo. Nadie lo escribe a mano. */
const MARCA_GRUPO = 'grupo:';

export function etiquetaDeGrupo(id: string): string {
  return `${MARCA_GRUPO}${id}`;
}

/** El grupo al que pertenece una capa, si pertenece a alguno. */
export function grupoDe(capa: Layer): string | null {
  const nombre = capa.name;
  if (!nombre || !nombre.startsWith(MARCA_GRUPO)) return null;
  return nombre.slice(MARCA_GRUPO.length);
}

/**
 * Marca varias capas como un grupo.
 *
 * Se conserva el nombre anterior de la capa dentro del propio grupo? No: el
 * nombre es la marca del grupo y punto. Guardar los dos exigiria un campo mas
 * en el esquema, y el nombre de una capa suelta es informacion que el cliente
 * casi nunca ha escrito.
 */
export function agrupar(capas: Layer[], seleccionadas: string[], idGrupo: string): Layer[] {
  if (seleccionadas.length < 2) return capas;
  const etiqueta = etiquetaDeGrupo(idGrupo);
  return capas.map((c) => (seleccionadas.includes(c.id) ? ({ ...c, name: etiqueta } as Layer) : c));
}

/** Deshace el grupo de las capas indicadas. */
export function desagrupar(capas: Layer[], idGrupo: string): Layer[] {
  return capas.map((c) => {
    if (grupoDe(c) !== idGrupo) return c;
    const { name, ...resto } = c as Layer & { name?: string };
    void name;
    return resto as Layer;
  });
}

/** Todas las capas del grupo al que pertenece esta, incluida ella. */
export function companerasDeGrupo(capas: Layer[], capaId: string): string[] {
  const capa = capas.find((c) => c.id === capaId);
  const grupo = capa ? grupoDe(capa) : null;
  if (!grupo) return capaId ? [capaId] : [];
  return capas.filter((c) => grupoDe(c) === grupo).map((c) => c.id);
}

export interface Zona {
  widthMm: number;
  heightMm: number;
}

/**
 * Lleva un diseno entero de una zona a otra.
 *
 * Se escala por el lado MAS APRETADO y luego se centra. Escalar cada eje por su
 * cuenta deformaria el diseno —un escudo saldria ovalado—, y eso en una prenda
 * se ve. Si sobra sitio en el otro eje, mejor que sobre.
 *
 * Nunca AMPLIA por encima del original: llevar un diseno de una taza a una
 * sudadera no significa que el cliente quiera el logotipo a 250 mm. Si cabe tal
 * cual, se deja tal cual y se centra.
 */
/**
 * Las capas listas para OTRA zona.
 *
 * Entre cajas IGUALES la copia es literal: un logotipo colocado arriba a la
 * izquierda del pecho tiene que aparecer arriba a la izquierda de la espalda.
 * `llevarAZona` CENTRA el conjunto —es lo correcto al insertar una plantilla—
 * pero al copiar movería lo que el cliente ya colocó donde quería.
 *
 * Solo cuando la caja cambia de verdad (el bolsillo) se encaja y centra.
 */
export function capasParaZona(capas: Layer[], origen: Zona, destino: Zona): Layer[] {
  const mismaCaja = Math.abs(origen.widthMm - destino.widthMm) < 0.5 && Math.abs(origen.heightMm - destino.heightMm) < 0.5;
  if (mismaCaja) return capas.map((c) => ({ ...c }));
  return llevarAZona(capas, origen, destino).capas;
}

export function llevarAZona(
  capas: Layer[],
  origen: Zona,
  destino: Zona,
): { capas: Layer[]; factor: number; seRecorto: boolean } {
  if (capas.length === 0) return { capas, factor: 1, seRecorto: false };

  const cajas = capas.map((c) => layerBBoxMm(c));
  const minX = Math.min(...cajas.map((b) => b.minX));
  const minY = Math.min(...cajas.map((b) => b.minY));
  const maxX = Math.max(...cajas.map((b) => b.maxX));
  const maxY = Math.max(...cajas.map((b) => b.maxY));
  const ancho = maxX - minX;
  const alto = maxY - minY;

  const factor = Math.min(1, ancho > 0 ? destino.widthMm / ancho : 1, alto > 0 ? destino.heightMm / alto : 1);

  // Centro del conjunto YA escalado, para poder centrarlo en la zona nueva.
  const nuevoAncho = ancho * factor;
  const nuevoAlto = alto * factor;
  const desplazamientoX = (destino.widthMm - nuevoAncho) / 2 - minX * factor;
  const desplazamientoY = (destino.heightMm - nuevoAlto) / 2 - minY * factor;

  const movidas = capas.map((capa) => {
    const t = capa.transform;
    const escalada = {
      a: t.a * factor,
      b: t.b * factor,
      c: t.c * factor,
      d: t.d * factor,
      e: t.e * factor + desplazamientoX,
      f: t.f * factor + desplazamientoY,
    };

    // El cuerpo del texto y los grosores viven en mm y NO estan en la matriz:
    // hay que escalarlos a mano o el texto quedaria del mismo tamano en una
    // zona la mitad de grande.
    if (capa.kind === 'text') {
      return {
        ...capa,
        transform: escalada,
        sizeMm: capa.sizeMm * factor,
        lineHeightMm: capa.lineHeightMm * factor,
        ...(capa.outline ? { outline: { ...capa.outline, widthMm: capa.outline.widthMm * factor } } : {}),
        ...(capa.arc ? { arc: { ...capa.arc, radiusMm: capa.arc.radiusMm * factor } } : {}),
      } as Layer;
    }
    if (capa.kind === 'shape' && capa.strokeWidthMm !== undefined) {
      return { ...capa, transform: escalada, strokeWidthMm: capa.strokeWidthMm * factor } as Layer;
    }
    return { ...capa, transform: escalada } as Layer;
  });

  return { capas: movidas, factor, seRecorto: factor < 1 };
}

/**
 * Que capas se quedarian fuera de la zona destino.
 *
 * Con `llevarAZona` no deberia quedarse ninguna, pero se comprueba igual: es la
 * lista que hay que ENSENARLE al cliente antes de darle por bueno el traslado,
 * y darla por hecha seria confiar en un calculo en vez de en el resultado.
 */
export function capasFueraDe(capas: Layer[], zona: Zona): string[] {
  return capas
    .filter((capa) => {
      const b = layerBBoxMm(capa);
      return b.minX < -0.01 || b.minY < -0.01 || b.maxX > zona.widthMm + 0.01 || b.maxY > zona.heightMm + 0.01;
    })
    .map((c) => c.id);
}
